import dataStorageManager, { BucketDefaultIDs } from '../../managers/data/DataStorageManager'
import { IUser, UserRoles } from './UsersServices'
import { IRequest, RequestObjectType, RequestStatus } from './RequestsServices'
import { IRolAlternativo } from './InterinosServices'
import { IArmador } from './ArmadoresServices'
import { IEmbarcacion } from './EmbarcacionesServices'
import { IEsporadicoView, EsporadicoEstado } from '../../data/EsporadicoProperties'

const MATCH_TOLERANCE_MS = 5000 // tolerancia hacia atrás por desfase de reloj al parear trabajo ↔ asociación

/**
 * Pure function: compone la vista de esporádicos a partir de los buckets base.
 *
 * Separa las solicitudes de cada usuario rol=7 en categorías:
 *   - solicitudBase: tipo='esporadico' + id_objeto_2 NULL. Una sola.
 *   - solicitudesTrabajo: tipo='esporadico' + id_objeto_2 = rol_alternativo.id. Desc.
 *   - solicitudesAsociacionArmador / Embarcacion: tipo='asociacion_esporadico' con
 *     tipo_objeto_2 = 'armador' | 'embarcacion'.
 *
 * El cargo se hereda de rol_alternativo (con su horario, logica_credencial, etc.).
 * `esVigente` es agregado: true si existe AL MENOS UN trabajo activo (APPROVED + caducidad
 * futura), no solo el último. `trabajosActivos` expone ese subconjunto (la base es siempre
 * APPROVED y no cuenta como trabajo).
 *
 * Referencia: RN36-RN42 + §"Modelo de solicitudes" del doc evolutivo.
 */
export function buildEsporadicosView(
  users: IUser[],
  solicitudes: IRequest[],
  rolesAlternativos: IRolAlternativo[],
  armadores: IArmador[],
  embarcaciones: IEmbarcacion[],
): IEsporadicoView[] {
  const now = new Date()
  const rolesById = new Map(rolesAlternativos.map((r) => [r.id, r]))
  const armadoresById = new Map(armadores.map((a) => [a.id, a]))
  const embarcacionesById = new Map(embarcaciones.map((e) => [e.id, e]))

  return users
    .filter((u) => u.rol === UserRoles.ESPORADICO)
    .map<IEsporadicoView>((u) => {
      // OJO: el backend devuelve `id_objeto` como NÚMERO y getAllSolicitudes lo copia sin
      // convertir (a diferencia de id_objeto_2, que sí pasa por .toString()). Aunque
      // IRequest.id_objeto está tipado como string, en runtime es number → comparar con
      // `=== idObj` (string) daba SIEMPRE false y dejaba la vista vacía ("Sin trabajo").
      // Normalizamos ambos lados a string para el match.
      const idObj = String(u.id_usuario)
      const propiasEsporadico = solicitudes
        .filter((s) => s.tipo_objeto === RequestObjectType.Esporadico && String(s.id_objeto) === idObj)
        .sort((a, b) => +new Date(b.fecha_solicitud) - +new Date(a.fecha_solicitud))
      const propiasAsoc = solicitudes.filter(
        (s) => s.tipo_objeto === RequestObjectType.AsociacionEsporadico && String(s.id_objeto) === idObj,
      )
      const asocArmador = propiasAsoc
        .filter((s) => s.tipo_objeto_2 === 'armador')
        .sort((a, b) => +new Date(b.fecha_solicitud) - +new Date(a.fecha_solicitud))
      const asocEmbarcacion = propiasAsoc
        .filter((s) => s.tipo_objeto_2 === 'embarcacion')
        .sort((a, b) => +new Date(b.fecha_solicitud) - +new Date(a.fecha_solicitud))

      const solicitudBase = propiasEsporadico.find((s) => !s.id_objeto_2)
      const solicitudesTrabajo = propiasEsporadico.filter((s) => !!s.id_objeto_2)
      const ultimaTrabajo = solicitudesTrabajo[0]

      // Asociaciones de la última solicitud de trabajo. Las asociaciones se insertan
      // SIEMPRE justo después de su trabajo (mismo "asignar trabajo"), por lo que su
      // fecha_solicitud es >= la del trabajo. Como `ultimaTrabajo` es el más reciente, le
      // pertenecen todas las asociaciones creadas en/después de él (con una pequeña
      // tolerancia hacia atrás por desfase de reloj). NO se usa cota superior: las
      // inserciones encadenadas recargan el bucket entre cada una y pueden tardar >5s,
      // por lo que la ventana ±5s anterior dejaba la asociación fuera → trabajo "sin
      // armador/embarcación".
      let ultArmador: IRequest[] = []
      let ultEmbarc: IRequest[] = []
      if (ultimaTrabajo) {
        const t = +new Date(ultimaTrabajo.fecha_solicitud)
        ultArmador = asocArmador.filter((a) => +new Date(a.fecha_solicitud) - t >= -MATCH_TOLERANCE_MS)
        ultEmbarc = asocEmbarcacion.filter((a) => +new Date(a.fecha_solicitud) - t >= -MATCH_TOLERANCE_MS)
      }

      const idRolAlternativo = ultimaTrabajo?.id_objeto_2 ? Number(ultimaTrabajo.id_objeto_2) : undefined
      const rolAlternativoData = idRolAlternativo ? rolesById.get(idRolAlternativo) : undefined

      const ultimosArmadores = ultArmador
        .map((a) => (a.id_objeto_2 ? armadoresById.get(Number(a.id_objeto_2)) : undefined))
        .filter((a): a is IArmador => !!a)
      const ultimasEmbarcaciones = ultEmbarc
        .map((a) => (a.id_objeto_2 ? embarcacionesById.get(Number(a.id_objeto_2)) : undefined))
        .filter((e): e is IEmbarcacion => !!e)

      // Vigencia AGREGADA: el esporádico está vigente si tiene AL MENOS UN trabajo activo
      // (APPROVED + caducidad futura), no solo el último. Un trabajo deja de estar activo
      // por revocación manual (estado→REJECTED) o por caducidad de fecha; mientras otro
      // siga vigente, el esporádico lo está. La fila muestra cuántos hay (trabajosActivos).
      const trabajosActivos = solicitudesTrabajo.filter(
        (s) => s.estado === RequestStatus.APPROVED && +new Date(s.fecha_caducidad) > +now,
      )
      const esVigente = trabajosActivos.length > 0

      // Estado mostrado: VIGENTE si hay algún activo; si no, SOLICITADO cuando hay alguna
      // pendiente; en caso contrario el estado terminal lo define el trabajo más reciente.
      let estado: EsporadicoEstado
      if (!ultimaTrabajo) {
        estado = EsporadicoEstado.SIN_SOLICITUDES
      } else if (esVigente) {
        estado = EsporadicoEstado.VIGENTE
      } else if (solicitudesTrabajo.some((s) => s.estado === RequestStatus.PENDING)) {
        estado = EsporadicoEstado.SOLICITADO
      } else if (ultimaTrabajo.estado === RequestStatus.REJECTED) {
        estado = ultimaTrabajo.fecha_respuesta ? EsporadicoEstado.REVOCADO : EsporadicoEstado.RECHAZADO
      } else if (ultimaTrabajo.estado === RequestStatus.APPROVED) {
        estado = EsporadicoEstado.CADUCADO
      } else {
        estado = EsporadicoEstado.SIN_SOLICITUDES
      }

      return {
        ...u,
        solicitudBase,
        solicitudesTrabajo,
        trabajosActivos,
        solicitudesAsociacionArmador: asocArmador,
        solicitudesAsociacionEmbarcacion: asocEmbarcacion,
        ultimaSolicitudTrabajo: ultimaTrabajo,
        ultimasAsociacionArmador: ultArmador,
        ultimasAsociacionEmbarcacion: ultEmbarc,
        idRolAlternativo,
        rolAlternativoData,
        ultimosArmadores,
        ultimasEmbarcaciones,
        esVigente,
        estado,
      }
    })
}

export default class EsporadicosServices {
  private bucketID: string | null = null

  constructor() {
    this.bucketID = dataStorageManager.registerBucket<IEsporadicoView>(this.getAllEsporadicos.bind(this), {
      refreshRatio: 5 * 60 * 1000,
      bucketID: BucketDefaultIDs.ESPORADICOS,
    })
  }

  startup() {}

  /**
   * Loader del bucket derivado. Lee USERS + SOLICITUDES + ROLES_ALTERNATIVOS +
   * ARMADORES + EMBARCACIONES y compone. NO consume endpoint propio — backend
   * `op_select_esporadicos` queda pendiente como optimización futura.
   * El cargo se resuelve contra el catálogo rol_alternativo (FK en id_objeto_2)
   * y hereda horario / logica_credencial / nivel_credencial del cargo.
   */
  async getAllEsporadicos(): Promise<IEsporadicoView[]> {
    const [users, solicitudes, roles, armadores, embarcaciones] = await Promise.all([
      dataStorageManager.getBucketData<IUser>(BucketDefaultIDs.USERS),
      dataStorageManager.getBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES),
      dataStorageManager.getBucketData<IRolAlternativo>(BucketDefaultIDs.ROLES_ALTERNATIVOS),
      dataStorageManager.getBucketData<IArmador>(BucketDefaultIDs.ARMADORES),
      dataStorageManager.getBucketData<IEmbarcacion>(BucketDefaultIDs.EMBARCACIONES),
    ])
    return buildEsporadicosView(users, solicitudes, roles, armadores, embarcaciones)
  }

  /**
   * Búsqueda directa por RUT contra el bucket derivado.
   */
  getEsporadicoByRut(rut: string): IEsporadicoView | undefined {
    const data = dataStorageManager.getCurrentBucketData<IEsporadicoView>(BucketDefaultIDs.ESPORADICOS)
    return data.find((e) => e.rut === rut)
  }

  /**
   * Devuelve el historial completo de solicitudes de trabajo de un esporádico
   * (todas, ordenadas desc por fecha_solicitud). RN40: visualización del historial.
   * No incluye la solicitud base (que es interna).
   */
  getSolicitudesEsporadico(idUsuario: number): IRequest[] {
    const view = dataStorageManager
      .getCurrentBucketData<IEsporadicoView>(BucketDefaultIDs.ESPORADICOS)
      .find((e) => e.id_usuario === idUsuario)
    return view?.solicitudesTrabajo ?? []
  }
}
